import { v4 as uuidv4 } from 'uuid'
import { db, type ImageRecord } from './dexie'
import { supabase, isSupabaseConfigured } from '../lib/supabase'
import { logger } from '../lib/logger'
import { captureError } from '../lib/errorReporting'

const log = logger('Images')

/** Custom URL scheme prefix for local images */
export const IMAGE_URL_PREFIX = 'yellowpad://image/'

/** Regex to match yellowpad://image/{uuid} in HTML src attributes */
const IMAGE_URL_REGEX = /yellowpad:\/\/image\/([\w-]+)/g

/**
 * Save an image blob to IndexedDB.
 * Returns the generated image ID.
 */
export async function saveImage(
  noteId: string,
  data: Blob,
  mimeType: string
): Promise<string> {
  const id = uuidv4()
  const record: ImageRecord = {
    id,
    note_id: noteId,
    data,
    mime_type: mimeType,
    size: data.size,
    created_at: new Date().toISOString(),
    synced_at: null,
    user_id: null,
  }
  await db.images.add(record)
  return id
}

const IMAGE_BUCKET = 'note-images'

/**
 * Get a single image record by ID.
 * If the image is not found locally, attempts to fetch it from Supabase Storage
 * (lazy pull). Downloaded images are cached in local IndexedDB for future use.
 */
export async function getImage(imageId: string): Promise<ImageRecord | undefined> {
  const local = await db.images.get(imageId)
  if (local) return local

  // Try to fetch from server if Supabase is configured
  if (!isSupabaseConfigured) return undefined

  return downloadImageFromServer(imageId)
}

/**
 * Download an image from Supabase Storage and cache it in local IndexedDB.
 * Looks up the image metadata first, then downloads the blob.
 */
async function downloadImageFromServer(imageId: string): Promise<ImageRecord | undefined> {
  try {
    // Look up metadata from the images table
    const { data: meta, error: metaError } = await supabase
      .from('images')
      .select('*')
      .eq('id', imageId)
      .single()

    if (metaError || !meta) {
      log.info('Image not found on server:', imageId)
      return undefined
    }

    // Download blob from Supabase Storage
    const { data: blob, error: downloadError } = await supabase.storage
      .from(IMAGE_BUCKET)
      .download(meta.storage_path)

    if (downloadError || !blob) {
      captureError(new Error(downloadError?.message ?? 'Unknown download error'), { source: 'images.download', extra: { imageId } })
      return undefined
    }

    // Cache in local IndexedDB
    const record: ImageRecord = {
      id: meta.id,
      note_id: meta.note_id,
      data: blob,
      mime_type: meta.mime_type,
      size: meta.size,
      created_at: meta.created_at,
      synced_at: new Date().toISOString(),
      user_id: meta.user_id,
    }

    await db.images.put(record)
    log.info('Downloaded and cached image:', imageId)
    return record
  } catch (error) {
    captureError(error, { source: 'images.fetch', extra: { imageId } })
    return undefined
  }
}

/**
 * Get all images belonging to a note.
 */
export async function getImagesForNote(noteId: string): Promise<ImageRecord[]> {
  return db.images.where('note_id').equals(noteId).toArray()
}

/**
 * Delete all images belonging to a note.
 */
export async function deleteImagesForNote(noteId: string): Promise<void> {
  await db.images.where('note_id').equals(noteId).delete()
}

/**
 * Resolve yellowpad://image/{id} URLs in HTML to blob URLs.
 * Returns the updated HTML and a list of created blob URLs (for later revocation).
 */
export async function resolveImageUrls(
  html: string
): Promise<{ html: string; blobUrls: string[] }> {
  // Collect all image IDs from the HTML
  const imageIds: string[] = []
  let match: RegExpExecArray | null
  const regex = new RegExp(IMAGE_URL_REGEX.source, 'g')

  while ((match = regex.exec(html)) !== null) {
    imageIds.push(match[1])
  }

  if (imageIds.length === 0) {
    return { html, blobUrls: [] }
  }

  // Load all images in parallel
  const records = await Promise.all(
    imageIds.map((id) => db.images.get(id))
  )

  // Build a map from image ID to blob URL
  const blobUrls: string[] = []
  const urlMap = new Map<string, string>()

  for (let i = 0; i < imageIds.length; i++) {
    const record = records[i]
    if (record?.data) {
      const blobUrl = URL.createObjectURL(record.data)
      urlMap.set(imageIds[i], blobUrl)
      blobUrls.push(blobUrl)
    }
  }

  // Replace yellowpad:// URLs with blob URLs
  const resolvedHtml = html.replace(
    IMAGE_URL_REGEX,
    (_fullMatch, imageId: string) => {
      return urlMap.get(imageId) || `${IMAGE_URL_PREFIX}${imageId}`
    }
  )

  return { html: resolvedHtml, blobUrls }
}

/**
 * Convert blob URLs in HTML back to yellowpad:// URLs for storage.
 * This is called before saving note content to Dexie.
 */
export function persistImageUrls(
  html: string,
  blobToIdMap: Map<string, string>
): string {
  let result = html
  for (const [blobUrl, imageId] of blobToIdMap) {
    result = result.replaceAll(blobUrl, `${IMAGE_URL_PREFIX}${imageId}`)
  }
  return result
}

/**
 * Revoke blob URLs to free memory.
 */
export function revokeImageUrls(blobUrls: string[]): void {
  for (const url of blobUrls) {
    try {
      URL.revokeObjectURL(url)
    } catch {
      // Ignore errors from already-revoked URLs
    }
  }
}
